<template>
  <div class="base-table-wrapper">
    <table class="base-table">
      <thead>
        <tr>
          <th v-for="column in columns" :key="column.key" :class="column.class">
            <span class="th-label">{{ column.label }}</span>
          </th>
          <th v-if="hasActions" class="actions-column">Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="loading">
          <td :colspan="columns.length + (hasActions ? 1 : 0)" class="loading-cell">
            Chargement...
          </td>
        </tr>
        <tr v-else-if="items.length === 0">
          <td :colspan="columns.length + (hasActions ? 1 : 0)" class="empty-cell">
            Aucun élément trouvé
          </td>
        </tr>
        <tr
          v-else
          v-for="item in items"
          :key="resolveItemKey(item)"
          :class="['table-row', getRowClass(item)]"
        >
          <td v-for="column in columns" :key="column.key" :class="column.class">
            <slot :name="`cell-${column.key}`" :item="item" :value="getValue(item, column.key)">
              {{ formatValue(getValue(item, column.key), column) }}
            </slot>
          </td>
          <td v-if="hasActions" class="actions-cell">
            <slot name="actions" :item="item">
              <BaseButton variant="ghost" size="sm" @click="$emit('view', item)">
                Voir
              </BaseButton>
            </slot>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { computed, useSlots } from 'vue';
import BaseButton from './BaseButton.vue';

export interface TableColumn {
  key: string;
  label: string;
  class?: string;
  formatter?: (value: unknown) => string;
}

type TableItem = Record<string, unknown>;

const props = defineProps<{
  columns: TableColumn[];
  items: TableItem[];
  loading?: boolean;
  getItemKey?: (item: TableItem) => string | number;
  showActions?: boolean;
  rowClass?: (item: TableItem) => string | string[] | Record<string, boolean> | undefined;
}>();

defineEmits<{
  view: [item: TableItem];
}>();

const slots = useSlots();
const hasActions = computed(() => {
  return props.showActions !== false && (!!slots.actions || true);
});

function resolveItemKey(item: TableItem): string | number {
  if (props.getItemKey) {
    return props.getItemKey(item);
  }
  const id = item.id;
  if (typeof id === 'string' || typeof id === 'number') {
    return id;
  }
  const key = item.key;
  if (typeof key === 'string' || typeof key === 'number') {
    return key;
  }
  return Math.random();
}

function getRowClass(item: TableItem) {
  return props.rowClass ? props.rowClass(item) : undefined;
}

function getValue(item: TableItem, key: string): unknown {
  const keys = key.split('.');
  let value: unknown = item;
  for (const k of keys) {
    if (typeof value !== 'object' || value === null) {
      return undefined;
    }
    value = (value as Record<string, unknown>)[k];
    if (value === undefined || value === null) break;
  }
  return value;
}

function formatValue(value: unknown, column: TableColumn): string {
  if (value === null || value === undefined) return '-';
  if (column.formatter) {
    return column.formatter(value);
  }
  if (typeof value === 'boolean') {
    return value ? 'Oui' : 'Non';
  }
  if (value instanceof Date) {
    return value.toLocaleDateString('fr-FR');
  }
  return String(value);
}
</script>

<style scoped>
.base-table-wrapper {
  overflow: hidden;
  background: var(--color-bg-primary);
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-border-light);
  box-shadow: var(--shadow-sm);
}

.base-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-sm);
}

.base-table thead {
  background: var(--color-bg-secondary);
  border-bottom: 1px solid var(--color-border-light);
}

.base-table th {
  padding: var(--spacing-sm) var(--spacing-md);
  text-align: left;
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
  font-size: var(--font-size-xs);
  font-family: var(--font-family-mono);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.base-table tbody tr {
  border-bottom: 1px solid var(--color-border-light);
  transition: background-color var(--transition-fast);
}

.base-table tbody tr:last-child {
  border-bottom: none;
}

.base-table tbody tr:hover {
  background: var(--color-bg-secondary);
}

.base-table td {
  padding: var(--spacing-sm) var(--spacing-md);
  color: var(--color-text-primary);
}

.loading-cell,
.empty-cell {
  text-align: center;
  padding: var(--spacing-xl);
  color: var(--color-text-secondary);
}

.actions-column {
  width: 150px;
  text-align: right;
}

.actions-cell {
  text-align: right;
  white-space: nowrap;
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-xs);
  justify-content: center;
}
</style>
